import { forwardRef, type ComponentProps, type ReactNode } from 'react';
import clsx from 'clsx';
import { Card, IconButton, Switch, type Tone } from '../ui';

/**
 * Small building blocks shared by the settings pages so every card keeps the same rhythm:
 * stacked rows separated by hairlines, labelled sub-sections, danger zones and status pills.
 */

/** Vertical stack of rows separated by hairlines (switch rows, device rows, key/value rows). */
export function Rows({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx('divide-y divide-line/70', className)}>{children}</div>;
}

/** A labelled row: text on the left, control on the right. Use inside <Rows>. */
export function SettingRow({ label, description, control, icon, className, disabled }: { label: ReactNode; description?: ReactNode; control?: ReactNode; icon?: ReactNode; className?: string; disabled?: boolean }) {
  return (
    <div className={clsx('flex flex-wrap items-start justify-between gap-x-6 gap-y-3 py-4 first:pt-0 last:pb-0', disabled && 'opacity-60', className)}>
      <div className="flex min-w-[9rem] flex-1 basis-0 items-start gap-3">
        {icon && <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-sunken text-muted">{icon}</span>}
        <div className="min-w-0">
          <p className="font-medium text-ink">{label}</p>
          {description && <p className="mt-0.5 text-[0.8125rem] leading-relaxed text-muted">{description}</p>}
        </div>
      </div>
      {control && <div className="flex shrink-0 items-center gap-2 pt-0.5">{control}</div>}
    </div>
  );
}

/** Switch as a full row (label left, toggle right), for use inside <Rows>. */
export function SwitchRow(props: ComponentProps<typeof Switch> & { icon?: ReactNode; className?: string }) {
  const { icon, className, label, description, ...sw } = props;
  return (
    <SettingRow
      icon={icon}
      className={className}
      label={<label htmlFor={sw.id} className={clsx(!sw.disabled && 'cursor-pointer')}>{label}</label>}
      description={description}
      control={<Switch {...sw} />}
    />
  );
}

/** Labelled group inside a card body (e.g. "Identity", "History scope"). */
export function SubSection({ title, hint, actions, children, className }: { title: ReactNode; hint?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={clsx('space-y-4', className)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-[0.8125rem] font-semibold text-ink">{title}</h4>
          {hint && <p className="mt-0.5 text-[0.8125rem] leading-relaxed text-muted">{hint}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/** Micro uppercase label used above small stat values. */
export function MicroLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={clsx('text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle', className)}>{children}</p>;
}

/** A compact stat: micro label on top, value below. */
export function Stat({ label, value, hint, className }: { label: ReactNode; value: ReactNode; hint?: ReactNode; className?: string }) {
  return (
    <div className={clsx('min-w-0', className)}>
      <MicroLabel>{label}</MicroLabel>
      <p className="mt-1 truncate text-[0.875rem] font-medium text-ink">{value}</p>
      {hint && <p className="mt-0.5 truncate text-xs text-muted">{hint}</p>}
    </div>
  );
}

const DOT: Record<Tone, string> = {
  neutral: 'bg-subtle',
  primary: 'bg-primary',
  success: 'bg-success',
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-info',
  lime: 'bg-vivid-lime',
  yellow: 'bg-vivid-yellow',
  lavender: 'bg-vivid-lavender',
  salmon: 'bg-vivid-salmon',
  sky: 'bg-vivid-sky',
  mint: 'bg-vivid-mint',
};

/** Pill with a coloured status dot. */
export function StatusPill({ tone = 'neutral', children, pulse, className, title }: { tone?: Tone; children: ReactNode; pulse?: boolean; className?: string; title?: string }) {
  return (
    <span title={title} className={clsx('inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full border border-line/80 bg-surface px-2 text-[0.6875rem] font-semibold text-ink', className)}>
      <span className="relative flex h-2 w-2">
        {pulse && <span className={clsx('absolute inline-flex h-full w-full animate-ping rounded-full opacity-60', DOT[tone])} aria-hidden />}
        <span className={clsx('relative inline-flex h-2 w-2 rounded-full', DOT[tone])} aria-hidden />
      </span>
      {children}
    </span>
  );
}

/** Danger-zone card: red-tinted header tile and a danger border. */
export function DangerCard({ title, description, icon, actions, children, className }: { title: ReactNode; description?: ReactNode; icon: ReactNode; actions?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <Card className={clsx('!border-danger/30', className)}>
      <div className="flex flex-wrap items-start justify-between gap-3 px-6 pt-6">
        <div className="flex min-w-0 items-start gap-3.5">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] bg-danger-soft text-danger-ink" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)' }}>
            {icon}
          </div>
          <div className="min-w-0">
            <h2 className="text-[0.9375rem] font-semibold leading-snug text-ink">{title}</h2>
            {description && <p className="mt-1 text-[0.8125rem] leading-relaxed text-muted">{description}</p>}
          </div>
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {children}
    </Card>
  );
}

/** Icon-only button with the CSS tooltip (no native title, so there is only one tooltip). */
export const TipIconButton = forwardRef<HTMLButtonElement, ComponentProps<typeof IconButton> & { tipPlacement?: 'bottom' | 'bottom-end' | 'right' }>(function TipIconButton(
  { tipPlacement, className, label, ...rest },
  ref,
) {
  return (
    <IconButton
      ref={ref}
      label={label}
      data-tip={label}
      className={clsx('tip', tipPlacement === 'bottom-end' && 'tip-bottom-end', tipPlacement === 'right' && 'tip-right', className)}
      {...rest}
      title={undefined}
    />
  );
});

/** Soft inner surface for previews and read-only blocks (no border, so it never nests a border in a card). */
export function Well({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx('rounded-2xl bg-raised p-4', className)}>{children}</div>;
}

/** Right-aligned footer row for forms inside cards. */
export function FormFooter({ children, note, className }: { children: ReactNode; note?: ReactNode; className?: string }) {
  return (
    <div className={clsx('flex flex-wrap items-center justify-end gap-2 border-t border-line/70 pt-4', className)}>
      {note && <span className="mr-auto text-xs text-muted">{note}</span>}
      {children}
    </div>
  );
}
